import { LegalPage } from "./LegalPage";

/**
 * Terms of use.
 *
 * `matchLandingHero` renders the hero at the exact same headline and
 * paragraph settings as the homepage hero (see PageHeader.jsx for the
 * clamp / line-height / width numbers). The italic accent lands on
 * "Terms of use" so the whole title reads as one italic phrase in the
 * champagne accent.
 */
export default function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of use"
      highlightWord="use"
      subtitle="The rules that apply when you access or use the T-REX Network websites, documentation, and public interfaces."
      lastUpdated="19 August 2026"
      matchLandingHero
    >
      <p>
        These Terms of Use (<strong>&quot;Terms&quot;</strong>) form a binding agreement between you and <strong>Digital Asset Operational Services ISAC Ltd.</strong> (<strong>&quot;DAOS Ltd.&quot;</strong>, <strong>&quot;we&quot;</strong>, <strong>&quot;our&quot;</strong>) governing your access to and use of the T-REX Network websites, marketing pages, developer documentation, application programming interfaces, admin dashboards, and any related content or communications (the <strong>&quot;Services&quot;</strong>). By using any part of the Services you agree to these Terms.
      </p>
      <p>
        These Terms govern general access to and use of the Services described above. They do not govern Managed Services, participation in the T-REX Network, regulated services, tokenised asset transactions, or any application or service that is subject to separate contractual terms, which will be governed by the applicable separate contractual documentation.
      </p>

      <h2>1. Eligibility and account use</h2>
      <p>
        The Services are made available for informational and professional purposes. You must be at least 18 years old to use them. If you access the Services on behalf of an organisation, you confirm that you have authority to bind that organisation to these Terms.
      </p>
      <p>
        Where the Services provide an account or dashboard, you are responsible for keeping your credentials confidential and for all activity that takes place under your credentials. Contact us immediately if you suspect unauthorised use. Where personal information is processed in connection with the Services, such processing will be governed by the applicable privacy notice made available by DAOS Ltd.
      </p>

      <h2>2. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Services in a way that violates applicable law or regulation.</li>
        <li>Attempt to access, probe, or scan any non-public part of the Services.</li>
        <li>Interfere with or disrupt the Services, servers, or networks connected to them.</li>
        <li>Reverse engineer, decompile, or copy any portion of the Services except as permitted by law.</li>
        <li>Reproduce, mirror, or redistribute content from the Services for commercial purposes without our written permission.</li>
        <li>Use the Services to build a competing product, or to train a model on the T-REX corpus without a separate written licence.</li>
      </ul>

      <h2>3. Intellectual property</h2>
      <p>
        All content on the Services, including text, graphics, documentation, code samples, brand assets, and the T-REX name and logo, is owned by DAOS Ltd. or its licensors and is protected by copyright, trademark, and other intellectual property laws. Except as expressly permitted, no rights are granted to you under these Terms.
      </p>
      <p>
        Open standards such as ERC-3643 are published under their respective open-source licences and are not restricted by these Terms. Where a specific open-source licence applies to a repository, that licence governs your rights in that repository.
      </p>

      <h2>4. Third-party links and services</h2>
      <p>
        The Services may reference or provide access to third-party websites, documentation, products, applications, or services. We do not control those third parties and are not responsible for their content, policies, or practices. Your use of any third party product, application or service is governed by the terms of that third party.
      </p>

      <h2>5. No professional advice</h2>
      <p>
        Content on the Services is provided for general information only. It does not constitute legal, tax, accounting, investment, or regulatory advice, and it does not constitute an offer, solicitation, or recommendation to buy or sell any security, token, or financial instrument. Always consult qualified professionals before making decisions related to regulated assets.
      </p>

      <h2>6. Forward-looking statements</h2>
      <p>
        The Services may include forward-looking statements about DAOS Ltd., the T-REX ecosystem, product roadmaps, or market outcomes. These statements involve risk and uncertainty. Actual results may differ materially. We do not undertake to update forward-looking statements as circumstances change.
      </p>

      <h2>7. Disclaimer of warranties</h2>
      <p>
        The Services are provided on an <em>as is</em> and <em>as available</em> basis. To the fullest extent permitted by law, DAOS Ltd. disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, and accuracy of content.
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, DAOS Ltd. and its affiliates, officers, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, business opportunity, or goodwill, arising out of or related to your use of the Services. The aggregate liability of DAOS Ltd. arising out of or related to the Services is limited to one hundred euros (EUR 100).
      </p>

      <h2>9. Indemnity</h2>
      <p>
        You agree to indemnify and hold DAOS Ltd. harmless from any claim, demand, or expense, including reasonable legal fees, arising out of your breach of these Terms or your misuse of the Services.
      </p>

      <h2>10. Changes to the Services and Terms</h2>
      <p>
        We may update the Services or these Terms from time to time. Material changes will be communicated on the Services and, where appropriate, by direct notice. Your continued use after a change constitutes acceptance of the updated Terms. Changes to any separately contracted services will be governed by the applicable service agreement and any mandatory legal or regulatory requirements.
      </p>

      <h2>11. Termination</h2>
      <p>
        We may suspend or terminate your access to any part of the Services at any time, with or without notice, if you breach these Terms or use the Services in a way that harms DAOS Ltd., other users, or third parties.
      </p>

      <h2>12. Governing law and forum</h2>
      <p>
        These Terms are governed by the laws of Bermuda, without regard to conflict-of-laws principles. Any dispute arising out of or in connection with these Terms will be brought exclusively before the competent courts of Bermuda, save for any mandatory consumer protection provisions of the country where you are resident.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms can be sent to <a href="mailto:legal@t-rex.network">legal@t-rex.network</a> or to Digital Asset Operational Services ISAC Ltd., c/o Apex Corporate Services Ltd., Vallis Building, 4th Floor, 58 Par-la-Ville Road, Hamilton HM 11, Bermuda.
      </p>
    </LegalPage>
  );
}
